import React, { memo, useContext, useRef, useState } from 'react';
import { useAlert } from 'react-alert';
import { Button, Form, Overlay, Popover } from 'react-bootstrap';

import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import AuthContext from '../../authentication/AuthContext';
import axios from 'axios';
import ProductImage from '../ProductImage';
import { IconWrapper } from '../IconWrapper';

export default memo(function QuickAdd({ orderId, updateView }) {
    const cas = useContext(AuthContext);
    const alert = useAlert();

    const productCodeField = useRef();
    const qtyField = useRef();

    const [showPopover, setShowPopover] = useState(false);
    const [popoverTarget, setPopoverTarget] = useState(null);

    const [productCodeToSearch, setProductCodeToSearch] = useState('');
    const [quantity, setQuantity] = useState('');
    const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);
    const [searchResult, setSearchResult] = useState({});
    const [productFound, setProductFound] = useState(false);
    const [highlightMaxQuantity, setHighlightMaxQuantity] = useState(false);

    const handleQuickAddSearch = () => {
        if (productCodeToSearch !== '') {
            axios
                .get(
                    window.CONFIG['ws.activoc-web-order-api.products.summary']
                        .replace('{orderId}', orderId)
                        .replace('{productCode}', productCodeToSearch.toString()),
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(response => {
                    setShowPopover(true);
                    setProductFound(true);
                    setIsAddButtonDisabled(false);
                    setPopoverTarget(qtyField.current);
                    setHighlightMaxQuantity(false);

                    const json = response.data;
                    json.cost = json.cost.toFixed(2);
                    json.rsp = json.rsp.toFixed(2);
                    json.margin = json.margin.toFixed(2);

                    const orderQuantity = json.orderQuantity === 0 ? '1' : json.orderQuantity;

                    setSearchResult(json);
                    setQuantity(orderQuantity);
                    qtyField.current.select();
                })
                .catch(error => {
                    if (error.response?.status === 404) {
                        setShowPopover(true);
                        setProductFound(false);
                        setIsAddButtonDisabled(true);
                        setSearchResult({ message: error.response.data.message });
                        setPopoverTarget(productCodeField.current);
                        productCodeField.current.select();
                    } else {
                        alert.error('ERROR: Unable to retrieve search results. ' + error + '.');
                    }
                });
        }
    };

    const handleUpdateProductCode = productCode => {
        if (isInputValidated(productCode)) {
            setProductCodeToSearch(productCode);
        } else {
            setProductCodeToSearch('');
            setIsAddButtonDisabled(true);
        }
    };

    const handleUpdateQuantity = qty => {
        if (isInputValidated(qty)) {
            if (searchResult.supplierMaxOrderQuantity && qty > searchResult.supplierMaxOrderQuantity) {
                qty = searchResult.supplierMaxOrderQuantity;
                setHighlightMaxQuantity(true);
            } else {
                setHighlightMaxQuantity(false);
            }
            setQuantity(qty);
            setIsAddButtonDisabled(false);
        } else {
            setQuantity('');
            setIsAddButtonDisabled(true);
        }
    };

    const handleAddProduct = () => {
        // Let GTM know about form submission
        const dataLayer = window.dataLayer || [];
        dataLayer.push({
            event: 'gtm.formSubmit',
            form: 'quickAdd'
        });
        axios
            .put(
                window.CONFIG['ws.activoc-web-order-api.order.lines.add'].replace('{orderId}', orderId),
                {
                    orderId: orderId,
                    productCode: productCodeToSearch,
                    quantity: quantity
                },
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .then(() => {
                setQuantity('');
                setProductCodeToSearch('');
                setIsAddButtonDisabled(true);
                setShowPopover(false);
                productCodeField.current.focus();
                updateView();
                alert.success(`ADDED: ${productCodeToSearch} ${searchResult.description} to your order.`);
            })
            .catch(() => {
                alert.error('ERROR: Unable to add product ' + productCodeToSearch + '.');
            });
    };

    const isInputValidated = input => /^[1-9][0-9]*$/.test(input); // Input is numeric and does not begin with 0

    const popoverWithResult = (
        <Popover
            id={`popover-positioned-${`bottom`}`}
            className={'quickadd-popover'}
            data-testid={'popover-with-result'}
        >
            <div className={'quickadd-popover-header row'}>
                <div className={'col-md-3 vcenter quickadd-popover-image'}>
                    <ProductImage src={searchResult.image} />
                </div>
                <div className={'col-md-9 vcenter'}>
                    <div className={'row'}>
                        <div className="col-md-12">
                            <h6 className={'quickadd-popover-description'}>{searchResult.description}</h6>
                        </div>
                    </div>
                    <div className={'row'}>
                        <div className="col-md-12">
                            <span className={'quickadd-popover-barcode'}>{searchResult.barcode}</span>
                        </div>
                    </div>
                </div>
            </div>
            <Popover.Content>
                <div className={'row quickadd-popover-details'}>
                    <div className={'quickadd-popover-info col-md-4'}>
                        <h6>Unit size</h6>
                        <p>{searchResult.unitSize}</p>
                    </div>
                    <div className={'quickadd-popover-info col-md-4'}>
                        <h6>Case size</h6>
                        <p>{searchResult.caseSize}</p>
                    </div>
                    {searchResult.supplierMaxOrderQuantity && (
                        <div
                            className={
                                'quickadd-popover-info col-md-4' +
                                (highlightMaxQuantity ? ' quantity-highlight' : '')
                            }
                        >
                            <h6>Max Qty</h6>
                            <p>{searchResult.supplierMaxOrderQuantity}</p>
                        </div>
                    )}
                </div>
                <div className={'row'}>
                    <div className={'quickadd-popover-info col-md-4'}>
                        <h6>Invoice Cost</h6>
                        <p className={'green'}>£{searchResult.cost}</p>
                    </div>
                    <div className={'quickadd-popover-info col-md-4'}>
                        <h6>RSP</h6>
                        <p className={'green'}>£{searchResult.rsp}</p>
                    </div>
                    <div className={'quickadd-popover-info col-md-4'}>
                        <h6>Margin</h6>
                        <p className={'green'}>{searchResult.margin}%</p>
                    </div>
                </div>
            </Popover.Content>
        </Popover>
    );

    const popoverWithoutResult = (
        <Popover id={`popover-positioned-${`bottom`}`} data-testid={'popover-without-result'}>
            <Popover.Content>
                <span>{searchResult.message}</span>
            </Popover.Content>
        </Popover>
    );

    return (
        <div className={'quickadd-header-controls'}>
            <Form id={'form-quick-add'}>
                <Form.Control
                    id={'quick-add-input-product-code'}
                    type={'text'}
                    placeholder={'Product Code...'}
                    onChange={event => {
                        setQuantity('');
                        setIsAddButtonDisabled(true);
                        handleUpdateProductCode(event.target.value);
                    }}
                    onKeyPress={event => {
                        if (event.key === 'Enter') {
                            qtyField.current.focus();
                        }
                    }}
                    onBlur={() => setShowPopover(false)}
                    maxLength={6}
                    aria-label={'Quick Add'}
                    autoComplete={'off'}
                    ref={productCodeField}
                    value={productCodeToSearch}
                />
                <Form.Control
                    id={'quick-add-input-quantity'}
                    type={'text'}
                    placeholder={'Qty'}
                    onFocus={() => handleQuickAddSearch()}
                    onBlur={() => {
                        if (productFound) {
                            setShowPopover(false);
                        }
                    }}
                    onChange={event => handleUpdateQuantity(event.target.value)}
                    onKeyPress={event => {
                        if (event.key === 'Enter' && quantity !== '') {
                            handleAddProduct();
                        }
                    }}
                    maxLength={3}
                    aria-label={'Quick Add'}
                    autoComplete={'off'}
                    ref={qtyField}
                    value={quantity}
                    tabIndex={productCodeToSearch === '' ? -1 : undefined}
                />

                <Overlay show={showPopover} placement="bottom" target={popoverTarget}>
                    {productFound ? popoverWithResult : popoverWithoutResult}
                </Overlay>

                <Button
                    id={'quick-add-submit'}
                    variant={'primary'}
                    onClick={handleAddProduct}
                    className={'quick-add-submit'}
                    disabled={isAddButtonDisabled}
                >
                    <IconWrapper icon={faPlusCircle} />
                    Add
                </Button>
            </Form>
        </div>
    );
});
